import { useOutletContext, Link } from 'react-router-dom';
import {
  FileText, Receipt, Banknote, CheckCircle2, Upload,
  AlertCircle, Clock, ArrowRight, CalendarCheck, Circle
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import StatusBadge from '../../components/ui/StatusBadge';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import { formatMoneyCompact } from '../../utils/formatters';

// ─── Mock data (clearly labeled in UI) ───

const MOCK_STATEMENTS = [
  { id: 1, vendor: 'XL Parts', period: '04/07/26 → 04/13/26', uploaded: '2h ago', matched: 38, total: 42, amount: 4872.50, status: 'in_progress' },
  { id: 2, vendor: 'Advance Auto Parts', period: '04/07/26 → 04/13/26', uploaded: '4h ago', matched: 21, total: 24, amount: 1963.22, status: 'in_progress' },
  { id: 3, vendor: 'O\'Reilly First Call', period: '03/31/26 → 04/06/26', uploaded: '1d ago', matched: 47, total: 47, amount: 7103.15, status: 'resolved' },
  { id: 4, vendor: 'AutoZone Pro', period: '03/31/26 → 04/06/26', uploaded: '1d ago', matched: 12, total: 14, amount: 892.40, status: 'in_progress' },
];

const MOCK_COD_CHECKS = [
  { id: 1, store: 'Montgomery', vendor: 'FRED HASS NISSAN', check: '#65869', amount: 3582.80, scanned: 'today', status: 'new' },
  { id: 2, store: 'Montgomery', vendor: 'HELFMAN FORD', check: '#4095', amount: 247.62, scanned: 'today', status: 'new' },
  { id: 3, store: 'Montgomery', vendor: 'EXEL SUPPLY', check: '#4094', amount: 266.03, scanned: 'today', status: 'new' },
  { id: 4, store: 'Porter', vendor: 'Transtar', check: '#8821', amount: 1240.00, scanned: '1d ago', status: 'new' },
  { id: 5, store: 'Spring', vendor: 'Sonnax', check: '#5533', amount: 489.50, scanned: '1d ago', status: 'new' },
];

const WEEKLY_CLOSE = [
  { label: 'Statements uploaded (4/4 vendors)', done: true },
  { label: 'Statement line items matched', done: false, current: true },
  { label: 'COD checks entered to QB', done: false },
  { label: 'Bill pay review complete', done: false },
  { label: 'ACH payments scheduled', done: false },
];

// ─── Main page ───

export default function BookkeeperDashboardPage() {
  const { dashboard, loading } = useOutletContext();
  const codCount = MOCK_COD_CHECKS.filter((c) => c.status === 'new').length;
  const codTotal = MOCK_COD_CHECKS.reduce((s, c) => s + c.amount, 0);
  const stmtPending = MOCK_STATEMENTS.filter((s) => s.status !== 'resolved').length;
  const stmtUnmatched = MOCK_STATEMENTS.reduce((s, x) => s + (x.total - x.matched), 0);
  const creditsOutstanding = dashboard?.credits?.total_outstanding_usd || 0;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div>
      <PageHeader
        title={<>Morning, <span className="text-[var(--color-insight-600)]">Patricia</span></>}
        description={`${today} · Week of Apr 14–20 close in progress`}
        actions={
          <>
            <Button variant="ghost" leftIcon={<Upload size={16} />}>Upload statement</Button>
            <Button variant="primary" leftIcon={<Receipt size={16} />}>Enter COD check</Button>
          </>
        }
      />

      {/* Metrics row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 stagger-children">
        <Metric
          label="Unmatched statement items"
          value={stmtUnmatched}
          sub={`${stmtPending} statements open`}
          accent="action"
          icon={<FileText />}
        />
        <Metric
          label="COD checks to enter"
          value={codCount}
          sub={formatMoneyCompact(codTotal)}
          accent="insight"
          icon={<Receipt />}
        />
        <Metric
          label="Credits owed by vendors"
          value={formatMoneyCompact(creditsOutstanding)}
          sub={`${dashboard?.credits?.count || 0} open`}
          accent="recovery"
          icon={<RefreshArrow />}
          loading={loading}
        />
        <Metric
          label="Bill pay mismatches"
          value={7}
          sub="Tekmetric → QB sync issues"
          accent="danger"
          icon={<AlertCircle />}
        />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <StatementsCard statements={MOCK_STATEMENTS} />
          <CodChecksCard checks={MOCK_COD_CHECKS} />
        </div>

        <div className="space-y-6">
          <WeeklyCloseCard steps={WEEKLY_CLOSE} />
          <QuickTipsCard />
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, sub, accent = 'warm', icon, loading }) {
  const accents = {
    warm:     'bg-[var(--color-cream-50)] border-[var(--color-warm-100)]',
    recovery: 'bg-[var(--color-recovery-50)] border-[var(--color-recovery-100)]',
    action:   'bg-[var(--color-action-50)] border-[var(--color-action-100)]',
    insight:  'bg-[var(--color-insight-50)] border-[var(--color-insight-100)]',
    danger:   'bg-[var(--color-danger-50)] border-[var(--color-danger-100)]',
    success:  'bg-[var(--color-success-50)] border-[var(--color-success-100)]',
  };
  const iconColors = {
    warm: 'text-[var(--color-warm-500)]',
    recovery: 'text-[var(--color-recovery-500)]',
    action: 'text-[var(--color-action-500)]',
    insight: 'text-[var(--color-insight-500)]',
    danger: 'text-[var(--color-danger-500)]',
    success: 'text-[var(--color-success-500)]',
  };
  return (
    <div className={`p-5 rounded-[var(--radius-lg)] border ${accents[accent]} shadow-[var(--shadow-xs)]`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-[var(--radius-md)] bg-white/60 flex items-center justify-center ${iconColors[accent]} [&_svg]:w-[18px] [&_svg]:h-[18px]`}>
          {icon}
        </div>
      </div>
      <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)] mb-1">{label}</div>
      <div className="text-[28px] font-extrabold text-[var(--color-warm-900)] tracking-tight tabular-nums leading-none mb-1">
        {loading ? '—' : value}
      </div>
      <div className="text-[12px] text-[var(--color-warm-600)] tabular-nums">{sub}</div>
    </div>
  );
}

function StatementsCard({ statements }) {
  return (
    <Card padded={false}>
      <div className="p-5">
        <CardHeader
          title={
            <span className="flex items-center gap-2.5">
              <FileText size={18} className="text-[var(--color-action-500)]" />
              Statements in Progress
              <Badge tone="neutral" size="xs">Preview</Badge>
            </span>
          }
          description="Weekly vendor statements awaiting reconciliation"
          action={<Button variant="ghost" size="sm" rightIcon={<ArrowRight size={14} />}>View all</Button>}
        />
      </div>
      <ul className="divide-y divide-[var(--color-warm-100)]">
        {statements.map((s) => {
          const pct = Math.round((s.matched / s.total) * 100);
          const done = s.status === 'resolved';
          return (
            <li key={s.id} className="px-5 py-4 hover:bg-[var(--color-cream-100)] transition-colors cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[14px] font-semibold text-[var(--color-warm-900)]">{s.vendor}</span>
                    {done ? (
                      <Badge tone="success" size="xs">Reconciled</Badge>
                    ) : (
                      <Badge tone="action" size="xs">{s.total - s.matched} unmatched</Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--color-warm-500)] flex items-center gap-2">
                    <span>{s.period}</span>
                    <span className="text-[var(--color-warm-300)]">·</span>
                    <span>uploaded {s.uploaded}</span>
                  </div>
                  <div className="mt-2 h-1.5 bg-[var(--color-cream-200)] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${done ? 'bg-[var(--color-success-500)]' : 'bg-[var(--color-action-500)]'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-[var(--color-warm-500)] tabular-nums mt-1">
                    {s.matched} of {s.total} matched · {pct}%
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[14px] font-semibold text-[var(--color-warm-900)] tabular-nums">
                    ${s.amount.toFixed(2)}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function CodChecksCard({ checks }) {
  return (
    <Card padded={false}>
      <div className="p-5">
        <CardHeader
          title={
            <span className="flex items-center gap-2.5">
              <Receipt size={18} className="text-[var(--color-insight-500)]" />
              COD Checks from Digital Filing Cabinet
              <Badge tone="neutral" size="xs">Preview</Badge>
            </span>
          }
          description="Scanned checks awaiting QuickBooks entry. Source: Google Drive end-of-day scans."
          action={<Button variant="insight" size="sm" className="bg-[var(--color-insight-500)] hover:bg-[var(--color-insight-600)] text-white">Bulk enter</Button>}
        />
      </div>
      <table className="w-full text-[12px]">
        <thead className="bg-[var(--color-cream-100)] text-[var(--color-warm-500)] text-[10px] uppercase tracking-[0.8px]">
          <tr>
            <th className="text-left px-5 py-2 font-bold">Store</th>
            <th className="text-left py-2 font-bold">Vendor</th>
            <th className="text-left py-2 font-bold">Check</th>
            <th className="text-left py-2 font-bold">Scanned</th>
            <th className="text-right px-5 py-2 font-bold">Amount</th>
          </tr>
        </thead>
        <tbody>
          {checks.map((c) => (
            <tr key={c.id} className="border-t border-[var(--color-warm-100)] hover:bg-[var(--color-cream-100)]/50 transition-colors cursor-pointer">
              <td className="px-5 py-3 text-[var(--color-warm-800)] font-medium">{c.store}</td>
              <td className="py-3 text-[var(--color-warm-800)]">{c.vendor}</td>
              <td className="py-3 mono-nums text-[var(--color-warm-600)]">{c.check}</td>
              <td className="py-3 text-[var(--color-warm-500)]">{c.scanned}</td>
              <td className="px-5 py-3 text-right tabular-nums font-semibold text-[var(--color-warm-900)]">
                ${c.amount.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function WeeklyCloseCard({ steps }) {
  const done = steps.filter((s) => s.done).length;
  const pct = Math.round((done / steps.length) * 100);
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2.5">
            <CalendarCheck size={18} className="text-[var(--color-recovery-500)]" />
            Weekly Close
          </span>
        }
        description="Week of Apr 14 – 20"
      />
      <div className="mb-4">
        <div className="flex items-center justify-between text-[11px] text-[var(--color-warm-500)] mb-1.5">
          <span className="font-semibold uppercase tracking-wide">Progress</span>
          <span className="tabular-nums">{done}/{steps.length}</span>
        </div>
        <div className="h-2 bg-[var(--color-cream-200)] rounded-full overflow-hidden">
          <div
            className="h-full bg-[var(--color-recovery-500)] rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <ul className="space-y-2">
        {steps.map((step, i) => (
          <li key={i} className={`flex items-start gap-2.5 py-1.5 ${step.current ? 'bg-[var(--color-action-50)] -mx-3 px-3 rounded-[var(--radius-sm)]' : ''}`}>
            {step.done ? (
              <CheckCircle2 size={16} className="text-[var(--color-success-500)] shrink-0 mt-0.5" />
            ) : step.current ? (
              <div className="w-4 h-4 rounded-full border-2 border-[var(--color-action-500)] shrink-0 mt-0.5 bg-[var(--color-action-100)]">
                <div className="w-1.5 h-1.5 bg-[var(--color-action-500)] rounded-full m-auto mt-[3px]" />
              </div>
            ) : (
              <Circle size={16} className="text-[var(--color-warm-300)] shrink-0 mt-0.5" />
            )}
            <span className={`text-[13px] ${
              step.done ? 'text-[var(--color-warm-500)] line-through'
              : step.current ? 'text-[var(--color-warm-900)] font-semibold'
              : 'text-[var(--color-warm-700)]'
            }`}>
              {step.label}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function QuickTipsCard() {
  return (
    <Card>
      <CardHeader title="Tips" description="Little reminders to keep things clean" />
      <ul className="space-y-3 text-[12px] text-[var(--color-warm-700)]">
        <li className="flex gap-2">
          <span className="text-[var(--color-action-500)] mt-[3px]">•</span>
          <span>COD checks brought over from Tekmetric as A/P need re-categorization to parts-purchase account.</span>
        </li>
        <li className="flex gap-2">
          <span className="text-[var(--color-action-500)] mt-[3px]">•</span>
          <span>Credit memos on statements auto-advance refund status — verify against Parts to Return bucket.</span>
        </li>
        <li className="flex gap-2">
          <span className="text-[var(--color-action-500)] mt-[3px]">•</span>
          <span>Ask auditor before dismissing any unmatched line item over $100.</span>
        </li>
      </ul>
    </Card>
  );
}

function RefreshArrow() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>;
}
